import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { dashboardService } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const ParentDashboard: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { school } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardService.getStats();
        setStats(response.data.stats);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Layout title="Parent Dashboard">
        <div className="text-center py-8">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Parent Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Children Count */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>My Children</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.childrenCount || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Unread Notices */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unread Notices</p>
              <p className="text-3xl font-bold text-purple-600">{stats?.unreadNotices || 0}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Children Overview */}
      <div className={`mt-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
        <div className="p-6">
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Children Overview</h3>
          <div className="space-y-4">
            {/* Sample child card - replace with actual data */}
            <div className={`border rounded-lg p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>John Doe</h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Class 10-A • Roll No: 15</p>
                </div>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Active</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attendance</p>
                  <p className="text-lg font-semibold text-green-600">92%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Last Grade</p>
                  <p className="text-lg font-semibold text-blue-600">A+</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Fees</p>
                  <p className="text-lg font-semibold text-red-600">₹5,000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Updates */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Announcements</h3>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Parent-Teacher Meeting</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Scheduled for December 20th at 10:00 AM</p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Posted 2 days ago</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-2">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Exam Results Published</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Mid-term exam results are now available</p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Posted 5 days ago</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Fee Reminder</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quarterly fees due on December 15th</p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Posted 1 week ago</p>
            </div>
          </div>
        </div>

        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Quick Access</h3>
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/parent/results')}
              className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 text-left"
            >
              📊 Check Results
            </button>
            <button 
              onClick={() => navigate('/parent/attendance')}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 text-left"
            >
              📅 View Attendance
            </button>
            <button 
              onClick={() => navigate('/parent/fees')}
              className="w-full bg-yellow-600 text-white py-2 px-4 rounded hover:bg-yellow-700 text-left"
            >
              💳 Pay Fees
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ParentDashboard;
